<template>
    <div id="review-cart">
        <loading-overlay :active.sync="displayLoader" loader="dots" color="#1A74DB" height="30" width="100%" opacity="0.85"></loading-overlay>
        <div class="padding-bottom-4" v-if="productsInCart.length > 0 && webstoreContent">
            <checkout-header
                :categoryFilter="{display: false}"
                :shoppingBag="{display: false}">
            </checkout-header>
            <div class="row">
                <div class="col xs-12 md-10 md-offset-1 lg-6 lg-offset-3" id="review-offers-in-cart-container">
                    <offers-in-cart :editable="false"></offers-in-cart>
                </div>
            </div>
            <div class="flex flex-center align-center" id="checkout-button-container">
                <button id="checkout-button" v-if="webstoreContent.continueToShipping" @click="submitHandler">{{ webstoreContent.continueToShipping }}</button>
                <div id="subtotal">{{ webstoreContent.subtotal }}: {{ currencySymbol }}{{ subtotal.toLocaleString('en-US') }}</div>
            </div>
        </div>
    </div> 
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex';
    import axios from 'axios';
    import OffersInCart from '../offers-in-cart';
    import CheckoutHeader from '../../layouts/header';
    import ScrollToTop from '../../../../mixins/scrollToTop';
    import { analyticsTrack } from "../../../../plugins/segment";
    import { WebstoreResources } from '../../../../mixins/webstore-resources';
    import SalesforceResources from "../../../../mixins/salesforce-resources";
    import SegmentResources from "../../../../mixins/segment-resources";
    
    export default {
        mixins: [
            ScrollToTop, 
            WebstoreResources, 
            SalesforceResources, 
            SegmentResources, 
        ],
        components: {
            CheckoutHeader, 
            OffersInCart
        },

        data() {
            return {
                submissionInProgress: false,
            }
        },

        watch: {
            '$route': 'hideIntercom',
        },

        computed:{
            ...mapGetters([
                'currencySymbol',
                'activeProductKey',
                'activeOfferKey',
            ]),

            ...mapGetters('checkout', [
                'isSelfServiced'
            ]),

            ...mapGetters('content', [
                'webstoreContent'
            ]),

            ...mapGetters('shoppingCart', [
                'productsInCart',
                'consolidatedMmyData',
                'consolidatedVinData',
                'consolidatedManualSelectionData'
            ]),

            displayLoader() {
                return this.submissionInProgress;
            },

            quantityInCart: function() {
                return this.productsInCart.reduce((prev,next) => prev + next.quantity, 0);
            },

            subtotal: function() {
                return this.productsInCart.reduce((prev,next) => prev + this.calculateTotalPrice(next.salesPrice) * next.quantity, 0).toFixed(2);
            },
        },

        methods: {
            ...mapMutations([
                'setActiveProductKey',
            ]),

            hideIntercom(){
                this.$intercom.shutdown()
            },

            cleanCables(product) {
                // Removes the VIN / MMY data in efforts to condense the session object for large orders
                product.cables && Array.isArray(product.cables) && product.cables.forEach(cable => {
                    delete cable.customerInput;
                    delete cable.originalPayload;
                    delete cable.error;
                })
                return product
            },

            handleCMVGBundle(product) {
                // Clone allows the webstore product cart to remain the same if a user navigates back
                let cmProduct = structuredClone(product);

                // Remove VG product codes from the CM product object
                cmProduct.productCodes = cmProduct.productCodes.filter((code) => code.includes('CM'))

                // Find VG-LIC list price, sales price, and discount
                let VGListPrice = 0;
                let VGSalesPrice = 0;
                let VGDiscount = 0;
                let CMDiscount = 0;
                cmProduct.listPrice.forEach((value) => {
                    if (value.code === 'LIC-VG-ENT') {
                        VGListPrice = value.price
                    }
                });
                cmProduct.salesPrice.forEach((value) => {
                    if (value.code === 'LIC-VG-ENT') {
                        VGSalesPrice = value.price
                        VGDiscount = value.discount
                    } else if (value.code.includes('LIC-CM')) {
                        CMDiscount = value.discount
                    }
                });

                // Create a VG product object (no price, since price was included in the CM product)
                let vgProduct = {
                    "cables": cmProduct.cables,
                    "defaultPaymentTerm": cmProduct.defaultPaymentTerm,
                    "discount": VGDiscount,
                    "listPrice": VGListPrice,
                    "monthlyUnitPrice": false,
                    "offerKey": "License + Hardware",
                    "offerName": "VG54 License + Hardware",
                    "offerSubtext": "GPS, compliance (ELD), routing, maintenance, and diagnostic tools for an all-in-one fleet management solution.",
                    "offerType": "Fleet Management",
                    "productCodes": [
                        "LIC-VG-ENT",
                        "HW-VG54-NA-BUNDLE"
                    ],
                    "productKey": "VG54",
                    "quantity": cmProduct.quantity,
                    "salesPrice": VGSalesPrice,
                }

                // Clear the cbales from CM product (they are now included in the VG product)
                cmProduct.cables = {};
                cmProduct.discount = CMDiscount;
                cmProduct.listPrice = this.calculateTotalPrice(cmProduct.listPrice.filter((price) => price.code.includes("CM")));
                cmProduct.salesPrice = this.calculateTotalPrice(cmProduct.salesPrice.filter((price) => price.code.includes("CM")));
                return [cmProduct, vgProduct]
            },


            submitHandler: async function(event) {
                this.submissionInProgress = true;

                // Map productsInCart over to new a array
                let submittedProducts = []

                // Split up the CM/VG bundle into separate products for the backend to process
                this.productsInCart.forEach((product) => {
                    if (['CM32-VG54', 'CM31-VG54'].includes(product.productKey) && product.offerKey === 'License + Hardware') {
                        let cleanedNewProducts = this.handleCMVGBundle(product).map(product => this.cleanCables(product));
                        submittedProducts = submittedProducts.concat(cleanedNewProducts);
                    } else {
                        product.listPrice = this.calculateTotalPrice(product.listPrice);
                        product.salesPrice = this.calculateTotalPrice(product.salesPrice);
                        submittedProducts.push(this.cleanCables(structuredClone(product)));
                    }
                });

                try {
                    let response = await axios({
                        method: 'POST',
                        url: '/webstore/submit-cables-and-order',
                        headers: {'Content-type': 'application/json'},
                        data: {
                            shoppingCart: submittedProducts
                        }
                    });
                    if (response.status === 200) {
                        this.$router.push("/checkout/shipping");
                        return;
                    } else {
                        this.$router.push({path: '/message', query: {type: 'errorCheckingOut'}});
                        return;
                    }
                } catch(e) {
                    if (e.response.data === 'sessionExpired') {
                        this.$router.push({path: '/message', query: {type: 'sessionExpired'}});
                        return;
                    } else {
                        this.$router.push({path: '/message', query: {type: 'errorCheckingOut'}});
                        return;
                    }
                }
            },
        },

        mounted () {
            analyticsTrack("Review Final Bag", { "products": this.productsInCart })
            this.hideIntercom();
        }
    }
</script>

<style scoped>
    .flex {
        display: flex;
    }

    .align-center {
        align-items: center;
    }

    .flex-center {
        justify-content: center;
    }

    .flex-between {
        justify-content: space-between;
    }

    .flex-left {
        align-items: flex-start;
    }

    #review-cart {
        width: 100%;
        height: 100%;
        font-family: "Nitti Grotesk", sans-serif;
        font-style: normal;
        font-weight: normal;
        margin: 0 auto;
    }

    #review-offers-in-cart-container {
        background: #FFFFFF;
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        margin-top: 40px;
    }

    #subtotal {
        font-size: 28px;
        line-height: 32px;
        text-align: right;
        color: #000000;
        margin: 0;
        margin-left: 30px;
    }

    #cable-selection-container {
        margin-bottom: 40px;
    }

    #checkout-button {
        height: 50px;
        color: white;
        background: #1A74DB;
        border-radius: 4px;
        border: none;
        font-size: 20px;
        line-height: 20px;
        padding: 0px 40px;
    }

    #checkout-button:hover {
        cursor: pointer;
        background-color: #0d6ede;
        border: #0d6ede;
    }

    #checkout-button-container {
        width: 100vw;
        padding: 20px;
        background-color: white;
        position: fixed;
        bottom: 0;
        justify-content: center;
        box-shadow: 0px 4px 20px rgb(0 0 0 / 10%);
    }

    @media (max-width: 736px) {
        #checkout-button-container {
            flex-wrap: wrap;
        }
        #subtotal {
            font-size: 24px;
            margin-top: 15px;
            margin-left: 0;
        }
        #review-offers-in-cart-container {
            margin-bottom: 100px;
        }
    }

</style>
